import { useState, useEffect } from 'react';
import { ClipboardList, Clock, PlayCircle, CheckCircle, ChevronRight } from 'lucide-react';
import { getActiveTradePlans } from '../api/client';

function formatCurrency(value) {
  if (!value) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function ProgressBar({ percentage }) {
  const segments = 6;
  const filledSegments = Math.round((percentage / 100) * segments);
  
  return (
    <div className="flex gap-0.5">
      {[...Array(segments)].map((_, i) => (
        <div
          key={i}
          className={`w-2.5 h-1.5 rounded-sm ${
            i < filledSegments ? 'bg-amber-400' : 'bg-slate-600'
          }`}
        />
      ))}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === 'in_progress') {
    return <PlayCircle className="w-3 h-3 text-amber-400" />;
  }
  if (status === 'approved') {
    return <CheckCircle className="w-3 h-3 text-blue-400" />;
  }
  return <Clock className="w-3 h-3 text-slate-400" />;
}

export default function ActivePlansWidget({ onNavigate }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPlans() {
      try {
        const result = await getActiveTradePlans();
        setPlans(result.plans || []);
      } catch (err) {
        console.error('Error loading active plans:', err);
      } finally {
        setLoading(false);
      }
    }
    loadPlans();
  }, []);

  if (loading) {
    return (
      <div className="card">
        <h3 className="card-header">
          <ClipboardList className="w-5 h-5 text-amber-400" />
          Active Trade Plans
        </h3>
        <div className="flex items-center justify-center py-6">
          <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div className="card">
        <h3 className="card-header">
          <ClipboardList className="w-5 h-5 text-amber-400" />
          Active Trade Plans
        </h3>
        <div className="text-center py-6 text-slate-400">
          <p className="text-sm">No active trade plans</p>
          <p className="text-xs mt-1 text-slate-500">Create one via Claude Desktop</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="card-header">
        <ClipboardList className="w-5 h-5 text-amber-400" />
        Active Trade Plans
      </h3>
      
      <div className="space-y-3">
        {plans.slice(0, 3).map(plan => (
          <div 
            key={plan.id}
            className="p-3 bg-slate-700/50 rounded-lg hover:bg-slate-700/70 transition cursor-pointer"
            onClick={() => onNavigate?.('plans')}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <StatusIcon status={plan.status} />
                  <span className="text-sm font-medium text-slate-200 truncate">
                    {plan.name}
                  </span>
                </div>
                <div className="text-xs text-slate-400">
                  {plan.status === 'draft' && 'Waiting for approval'}
                  {plan.status === 'approved' && 'Ready to execute'}
                  {plan.status === 'in_progress' && plan.next_item && `Next: ${plan.next_item}`}
                  {plan.status === 'in_progress' && !plan.next_item && `${plan.executed_items}/${plan.total_items} executed`}
                </div>
              </div>
              
              <div className="flex flex-col items-end gap-1">
                <ProgressBar percentage={plan.fill_percentage || 0} />
                <span className="text-xs text-slate-400">
                  {plan.executed_items}/{plan.total_items}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {plans.length > 3 && (
        <button
          onClick={() => onNavigate?.('plans')}
          className="mt-3 w-full flex items-center justify-center gap-1 text-sm text-slate-400 hover:text-slate-200 transition"
        >
          View all {plans.length} plans
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}


